/**
 * Row of secondary actions next to the BigRecordButton — Pro Tools transport
 * sub-buttons (Preview / Last take / Pickup / Next).
 *
 *   ▶ Preview  (Space) — play just the reference voice, no record
 *   ⏯ Last     (L)     — play the actor's last take
 *   ↻ Pickup   (R)     — re-record current cue, version bump
 *   → Next     (N)     — advance to next cue (only after review)
 *
 * Each button is a PT-style chrome bevel with icon + label + Kbd shortcut chip.
 */
import { cx } from './cx'
import type { Phase } from './state'

export interface SecondaryButtonsProps {
  phase: Phase
  hasLastTake: boolean
  onPreview: () => void
  onPlayLast: () => void
  onPickup: () => void
  onNext: () => void
}

interface ActionButtonProps {
  icon: string
  label: string
  shortcut: string
  onClick: () => void
  disabled?: boolean
  title?: string
}

function ActionButton({ icon, label, shortcut, onClick, disabled, title }: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cx(
        'pt-btn',
        'flex flex-col items-center justify-center gap-0.5 px-3 py-2 min-w-[78px]',
      )}
    >
      <span aria-hidden="true" className="text-[15px] leading-none text-pt-text">
        {icon}
      </span>
      <span className="text-[11px] font-semibold leading-none text-pt-text">{label}</span>
      <span className="pt-kbd mt-0.5">{shortcut}</span>
    </button>
  )
}

export function SecondaryButtons({
  phase,
  hasLastTake,
  onPreview,
  onPlayLast,
  onPickup,
  onNext,
}: SecondaryButtonsProps) {
  const busyPhases: ReadonlySet<Phase> = new Set([
    'recording',
    'three_pop',
    'qc_local',
    'upload',
    'qc_server',
  ])
  const isBusy = busyPhases.has(phase)
  return (
    <div className="flex justify-center gap-2">
      <ActionButton
        icon="▶"
        label="Preview"
        shortcut="Space"
        onClick={onPreview}
        disabled={isBusy}
      />
      <ActionButton
        icon="⏯"
        label="Last take"
        shortcut="L"
        onClick={onPlayLast}
        disabled={isBusy || !hasLastTake}
        title={hasLastTake ? 'Play your last take' : 'No previous take to play'}
      />
      <ActionButton
        icon="↻"
        label="Pickup"
        shortcut="R"
        onClick={onPickup}
        disabled={isBusy || !hasLastTake}
      />
      <ActionButton
        icon="→"
        label="Next"
        shortcut="N"
        onClick={onNext}
        disabled={isBusy && phase !== 'review'}
      />
    </div>
  )
}
